import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Search, MapPin, Loader2 } from 'lucide-react';

/**
 * AddressAutocomplete Component
 * Uses Photon API (OpenStreetMap) for free German address autocomplete
 * 
 * Props:
 * - value: current input value
 * - onChange: callback when input changes (receives string)
 * - onAddressSelect: callback when address is selected (receives full address object)
 * - placeholder: input placeholder text
 * - className: additional CSS classes
 * - disabled: disable the input
 * - required: mark as required
 */
const AddressAutocomplete = ({
  value = '',
  onChange,
  onAddressSelect,
  placeholder = 'Adresse eingeben...',
  className = '',
  disabled = false,
  required = false,
  label,
  showPLZCity = false // If true, also updates PLZ and city fields
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Sync external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch suggestions from Photon API
  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      // Photon API - free, no API key needed
      const response = await fetch(
        `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&lang=de&limit=6`
      );
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const formattedSuggestions = data.features
          .filter(f => f.properties.street || f.properties.name)
          .map((feature, index) => {
            const props = feature.properties;
            const street = props.street || props.name || '';
            const houseNumber = props.housenumber || '';
            const postcode = props.postcode || '';
            const city = props.city || props.town || props.village || props.municipality || '';
            const state = props.state || '';
            
            return {
              id: `${index}-${street}-${postcode}`,
              street: houseNumber ? `${street} ${houseNumber}` : street,
              streetOnly: street,
              houseNumber: houseNumber,
              postcode: postcode,
              city: city,
              state: state,
              formatted: `${houseNumber ? `${street} ${houseNumber}` : street}${postcode ? `, ${postcode}` : ''} ${city}`.trim(),
              coordinates: feature.geometry?.coordinates
            };
          });
        setSuggestions(formattedSuggestions);
        setShowDropdown(true);
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      console.error('Address search error:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    // Notify parent of text change
    if (onChange) {
      onChange(newValue);
    }

    // Debounce API calls
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 350);
  };

  // Handle suggestion selection
  const handleSelect = (suggestion) => {
    setInputValue(suggestion.street);
    setShowDropdown(false);
    setSuggestions([]);

    // Notify parent
    if (onChange) {
      onChange(suggestion.street);
    }
    if (onAddressSelect) {
      onAddressSelect(suggestion);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-300 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#9B1B30] focus:border-transparent pr-10"
          autoComplete="off"
          data-testid="address-autocomplete-input"
        />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
          {isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Search className="w-5 h-5" />
          )}
        </div>
      </div>

      {/* Suggestions Dropdown */}
      {showDropdown && suggestions.length > 0 && (
        <div 
          className="absolute z-[9999] w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl max-h-64 overflow-y-auto"
          style={{ zIndex: 9999 }}
          data-testid="address-suggestions"
        >
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.id}
              type="button"
              onClick={() => handleSelect(suggestion)}
              className="w-full px-4 py-3 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-200 last:border-b-0 transition-colors"
            >
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#9B1B30] flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-gray-900 font-medium truncate">
                    {suggestion.street}
                  </div>
                  <div className="text-sm text-gray-500 truncate">
                    {suggestion.postcode} {suggestion.city}
                    {suggestion.state && `, ${suggestion.state}`}
                  </div>
                </div>
              </div>
            </button>
          ))}
          <div className="px-4 py-2 text-xs text-gray-400 bg-gray-50 border-t border-gray-200">
            Powered by OpenStreetMap
          </div>
        </div>
      )}

      {/* No results message */}
      {showDropdown && inputValue.length >= 3 && suggestions.length === 0 && !isLoading && (
        <div 
          className="absolute z-[9999] w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl p-4 text-center text-gray-500"
          style={{ zIndex: 9999 }}
        >
          Keine Adressen gefunden
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
